import styles from './styles.module.scss';
import { useState } from 'react';
import { Nullable } from '@starsoft/common/interfaces';
import { CommonModalsKey } from '@starsoft/common/enums';
import { useTranslation } from '../../../../hooks';
import { useCurrentModal } from '../../../../hooks/modals/useCurrentModal';

/**
 * PaymentExperience Component
 * Renders a feedback UI with thumbs up/down buttons for users to rate their payment experience.
 * The component adapts its text based on whether it's shown in a deposit or withdrawal context.
 *
 * @component
 * @returns {JSX.Element} A container with description text and two feedback buttons
 *
 * @example
 * <PaymentExperience />
 */
export default function PaymentExperience() {
  const [isPositive, setIsPositive] = useState<Nullable<boolean>>(null);
  const { t } = useTranslation('deposit');
  const modal: string | undefined = useCurrentModal(CommonModalsKey);

  /**
   * Handles click events on the feedback buttons
   * @param {boolean} isPositive - Whether positive (true) or negative (false) feedback was given
   * @returns {void}
   *
   * @example
   * handleClick(true) // Sets positive feedback
   * handleClick(false) // Sets negative feedback
   */
  function handleClick(isPositive: boolean) {
    setIsPositive(isPositive);
  }

  return (
    <div className={styles.container}>
      <p className={styles.container__description}>
        {modal === CommonModalsKey.Deposit
          ? t('payment_experience')
          : t('withdraw:payment_experience')}
      </p>

      <div className={styles.container__row}>
        <button
          className={`${styles.container__button} ${styles['container__button--positive']} ${isPositive ? styles['container__button--positive--selected'] : ''}`}
          onClick={() => handleClick(true)}
        >
          {isPositive && (
            <div className={styles['container__button__particle-container']}>
              <div
                className={
                  styles['container__button__particle-container__particle']
                }
              />
            </div>
          )}
          <i className="fa-solid fa-thumbs-up" />
        </button>

        <button
          className={`${styles.container__button} ${styles['container__button--negative']} ${isPositive === false ? styles['container__button--negative--selected'] : ''}`}
          onClick={() => handleClick(false)}
        >
          {!isPositive && (
            <div className={styles['container__button__particle-container']}>
              <div
                className={
                  styles['container__button__particle-container__particle']
                }
              />
            </div>
          )}
          <i className="fa-solid fa-thumbs-up" />
        </button>
      </div>
    </div>
  );
}
